/**
 * Invite admin page (Phase 3 Action 42).
 *
 * Form: email + optional name. Submit → POST /:id/invite-admin.
 * Server response carries the magic link URL — the raw token shown
 * here is the ONLY chance to capture it; the DB only has the hash.
 *
 * Two-step UX: form first, then a "Link issued" panel with copy
 * affordance + expiry. Operator returns to tenant detail via the
 * Done button (no auto-navigate so they have time to copy).
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { apiPath } from '../api/baseUrl';
import { getStoredToken } from '../auth/AuthContext';

interface InviteResponse {
  data: {
    invite: {
      tenantUserId: string;
      email: string;
      magicLinkUrl: string;
      expiresAt: string;
    };
  };
}

async function postInvite(id: string, body: { email: string; name?: string }): Promise<InviteResponse> {
  const token = getStoredToken();
  const res = await fetch(apiPath(`/api/provisioning/tenants/${id}/invite-admin`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let message = `invite failed: ${res.status}`;
    try {
      const b = (await res.json()) as { message?: string };
      if (b.message) message = b.message;
    } catch {
      // ignore
    }
    throw new Error(message);
  }
  return res.json();
}

function formatExpiry(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export default function InviteAdmin() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id = '' } = useParams<{ id: string }>();

  // Re-issue flow lands here with ?email=...&name=... — prefill so the
  // operator just clicks Issue.
  const [search] = useSearchParams();
  const [email, setEmail] = useState(search.get('email') ?? '');
  const [name, setName] = useState(search.get('name') ?? '');
  const [copied, setCopied] = useState(false);
  const isReissue = Boolean(search.get('email'));

  const mutation = useMutation({
    mutationFn: (body: { email: string; name?: string }) => postInvite(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-admins', id] });
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const body: { email: string; name?: string } = { email: email.trim().toLowerCase() };
    if (name.trim()) body.name = name.trim();
    mutation.mutate(body);
  }

  async function handleCopy() {
    if (!mutation.data) return;
    try {
      await navigator.clipboard.writeText(mutation.data.data.invite.magicLinkUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // ignore — user can select the input manually
    }
  }

  const invite = mutation.data?.data.invite;
  const submitting = mutation.isPending;

  return (
    <div className="max-w-2xl">
      <div className="mb-6">
        <Link to={`/fleet/${id}`} className="text-sm text-slate-500 hover:text-slate-700">← Back to tenant</Link>
        <h1 className="mt-2 text-2xl font-semibold text-slate-900">
          {isReissue ? 'Re-issue magic link' : 'Invite admin'}
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          {isReissue
            ? 'Generate a fresh magic link for an existing admin. The previous link continues to expire on its own schedule.'
            : 'Provisioning wizard · Step 5 of 5 — Admin invite'}
        </p>
      </div>

      {mutation.isError && !invite && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {mutation.error.message}
        </div>
      )}

      {!invite && (
        <form onSubmit={handleSubmit} className="space-y-5 rounded-md border border-slate-200 bg-white p-6">
          <p className="text-sm text-slate-600">
            Send a one-time magic link to the customer's first admin. The link expires in 24 hours.
          </p>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-700">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@customer.com"
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              required
            />
          </div>

          <div>
            <label htmlFor="name" className="block text-sm font-medium text-slate-700">Name <span className="text-slate-400">(optional)</span></label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Alice Smith"
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => navigate(`/fleet/${id}`)}
              disabled={submitting}
              className="rounded-md border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !email.trim()}
              className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {submitting ? 'Issuing…' : isReissue ? 'Re-issue magic link' : 'Issue magic link'}
            </button>
          </div>
        </form>
      )}

      {invite && (
        <div className="space-y-5">
          <section className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
            Magic link issued for <strong>{invite.email}</strong>. An email was sent via the
            console stub — copy the link below if you need to deliver it manually.
          </section>

          <section className="rounded-md border border-slate-200 bg-white p-6">
            <h2 className="text-sm font-semibold text-slate-900">One-time magic link</h2>
            <p className="mt-1 text-xs text-amber-700">
              This is the only time you'll see this URL. The database only stores the hash. If you lose
              it, re-issue the invite to get a new one.
            </p>
            <div className="mt-3 flex items-stretch gap-2">
              <input
                type="text"
                readOnly
                value={invite.magicLinkUrl}
                onFocus={(e) => e.currentTarget.select()}
                className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-xs font-mono text-slate-700 shadow-sm"
              />
              <button
                type="button"
                onClick={handleCopy}
                className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <p className="mt-2 text-xs text-slate-500">Expires {formatExpiry(invite.expiresAt)}.</p>
          </section>

          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate(`/fleet/${id}`)}
              className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
